import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Clock3, CalendarClock, Clock, Loader2 } from 'lucide-react';
import { format, type Locale } from 'date-fns';

interface ContactAttempt {
  id: string;
  result_tag: string;
  attempted_at: string;
  notes: string | null;
}

const TAG_LABELS: Record<string, string> = {
  NA: 'Nicht angetroffen',
  KT: 'Kontakt/Termin',
  KI: 'Kein Interesse',
  NO: 'Nicht optimierbar',
  WIN: 'Gewonnen',
  HOT: 'Hot Lead',
  BK: 'Briefkasten',
};

function getTagLabel(tag: string): string {
  // tag can be comma-separated
  const parts = tag.split(',').map(t => TAG_LABELS[t.trim()] || t.trim());
  return parts.join(', ');
}

interface ContactAttemptsPopoverProps {
  unitId: string;
  currentCount: number;
  getLocale: () => Locale;
}

export const ContactAttemptsPopover = ({ unitId, currentCount, getLocale }: ContactAttemptsPopoverProps) => {
  const [attempts, setAttempts] = useState<ContactAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchAttempts = async () => {
    // Always re-fetch to show latest attempts (no stale cache)
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('d2d_contact_attempts')
        .select('id, result_tag, attempted_at, notes')
        .eq('unit_id', unitId)
        .order('attempted_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching contact attempts:', error);
        return;
      }
      setAttempts(data || []);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover onOpenChange={(open) => { if (open) fetchAttempts(); }}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[11px] font-medium cursor-pointer transition-all hover:scale-105 active:scale-95 border border-primary/20 whitespace-nowrap">
          <Clock3 className="h-3 w-3 shrink-0" />
          <span>{currentCount}. Versuch</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 max-h-[400px] overflow-y-auto" align="start">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock3 className="h-4 w-4 text-primary" />
            Kontaktversuche ({attempts.length || currentCount})
          </div>

          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && attempts.length === 0 && (
            <div className="text-xs text-muted-foreground italic border-l-2 border-muted pl-2 py-1">
              Keine Details gespeichert. Neue Versuche werden ab jetzt aufgezeichnet.
            </div>
          )}

          {!loading && attempts.length > 0 && (
            <div className="space-y-2">
              {attempts.map((attempt, index) => (
                <div key={attempt.id} className="bg-muted/50 rounded-md p-2.5 border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      {attempts.length - index}. Versuch
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {getTagLabel(attempt.result_tag)}
                    </Badge>
                  </div>
                  <div className="mt-1.5 text-sm">
                    <div className="flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{(() => {
                        const d = new Date(attempt.attempted_at);
                        const now = new Date();
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
                        const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                        if (day.getTime() === today.getTime()) return 'Heute';
                        if (day.getTime() === yesterday.getTime()) return 'Gestern';
                        return format(d, "EEEE", { locale: getLocale() });
                      })()}</span>
                      {', '}
                      {format(new Date(attempt.attempted_at), "dd.MM.yyyy", { locale: getLocale() })}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      {format(new Date(attempt.attempted_at), "HH:mm 'Uhr'", { locale: getLocale() })}
                    </div>
                  </div>
                  {attempt.notes && (
                    <div className="mt-1.5 text-xs text-muted-foreground bg-background/50 rounded px-2 py-1">
                      {attempt.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
